import React, { useState } from 'react';
import { SaveIcon, InfoIcon } from 'lucide-react';
import { User, MediaType } from '../data/types';
import { Header } from '../components/layout/Header';
import { Button } from '../components/ui/Button';
interface RateCardPageProps {
  user: User;
  onLogout: () => void;
  onNavigate: (view: any) => void;
  activityLog?: any;
  stores?: any[];
  brands?: any[];
  brandRequests?: any[];
  readOnly?: boolean;
}
const mediaTypes: MediaType[] = ['Digital Outposts', 'Digital Drive Aisle'];
const initialPrices: Record<MediaType, number> = {
  'Digital Outposts': 3250,
  'Digital Drive Aisle': 3750,
  'Digital Windows': 3500
};
export function RateCardPage({
  user,
  onLogout,
  onNavigate,
  activityLog,
  stores,
  brands,
  brandRequests,
  readOnly = false
}: RateCardPageProps) {
  const [prices, setPrices] = useState<Record<MediaType, number>>(() => ({
    ...initialPrices
  }));
  const [seasonalMultiplier, setSeasonalMultiplier] = useState<number>(1.0);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const handlePriceChange = (mediaType: MediaType, value: string) => {
    const numValue = parseInt(value.replace(/[^0-9]/g, ''), 10) || 0;
    setPrices(prev => ({
      ...prev,
      [mediaType]: numValue
    }));
    setSaveSuccess(false);
  };
  const handleSave = () => {
    setIsSaving(true);
    setTimeout(() => {
      console.log('Saved rate card:', {
        prices,
        seasonalMultiplier
      });
      setIsSaving(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    }, 800);
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0
    }).format(amount);
  };
  return <div className="min-h-screen w-full bg-white flex flex-col" data-id="element-2017">
      <Header user={user} onLogout={onLogout} currentView="rate-cards" onNavigate={onNavigate} stores={stores} brands={brands} brandRequests={brandRequests} activityEntries={activityLog?.entries} unreadCount={activityLog?.unreadCount} onMarkAllRead={activityLog?.markAllAsRead} onMarkRead={activityLog?.markAsRead} data-id="element-2018" />

      <div className="bg-surface-warm border-b border-border px-6 py-8" data-id="element-2019">
        <div className="max-w-5xl mx-auto flex items-center justify-between" data-id="element-2020">
          <div data-id="element-2021">
            <h1 className="text-2xl font-bold text-text-primary mb-2" data-id="element-2022">
              Rate Card Management
            </h1>
            <p className="text-text-secondary text-sm" data-id="element-2023">
              {readOnly ? 'View media slot pricing per media type.' : 'Manage media slot pricing per media type.'}
            </p>
          </div>
          {!readOnly && <Button variant="primary" onClick={handleSave} disabled={isSaving} className="flex items-center gap-2 bg-gray-900 hover:bg-gray-800" data-id="element-2024">
              <SaveIcon className="w-4 h-4" data-id="element-2025" />
              {isSaving ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save Changes'}
            </Button>}
        </div>
      </div>

      <main className="flex-1 max-w-5xl mx-auto w-full px-6 py-8 space-y-8" data-id="element-2026">
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 flex items-start gap-3" data-id="element-2027">
          <InfoIcon className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" data-id="element-2028" />
          <div className="text-sm text-blue-800" data-id="element-2029">
            <p className="font-semibold mb-1" data-id="element-2030">Pricing Rules</p>
            <p data-id="element-2031">
              {readOnly ? 'These are the current flat media fees set by Grapevine. Pricing is per media type, per store, per week. Contact your Grapevine account manager to request changes.' : 'These flat media fees are applied per media type, per store, per week. The seasonal multiplier is applied globally to all new bookings. Existing confirmed bookings will not be affected by rate card changes.'}
            </p>
          </div>
        </div>

        <div className="bg-white border border-border rounded-lg shadow-sm overflow-hidden" data-id="element-2032">
          <div className="px-6 py-4 border-b border-border bg-surface flex items-center justify-between" data-id="element-2033">
            <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2" data-id="element-2034">
              <span className="text-base font-bold text-text-secondary" data-id="element-2035">R</span>
              Media Fees (ZAR)
            </h2>
            <div className="flex items-center gap-3" data-id="element-2036">
              <label className="text-sm font-medium text-text-secondary" data-id="element-2037">
                Seasonal Multiplier:
              </label>
              {readOnly ? <span className="px-3 py-1.5 bg-surface-warm border border-border rounded-sm text-sm font-medium" data-id="element-2038">
                  {seasonalMultiplier.toFixed(1)}x
                </span> : <select value={seasonalMultiplier} onChange={e => {
              setSeasonalMultiplier(parseFloat(e.target.value));
              setSaveSuccess(false);
            }} className="px-3 py-1.5 border border-border rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-primary" data-id="element-2039">
                  <option value={0.8} data-id="element-2040">0.8x (Off-peak)</option>
                  <option value={1.0} data-id="element-2041">1.0x (Standard)</option>
                  <option value={1.2} data-id="element-2042">1.2x (Peak)</option>
                  <option value={1.5} data-id="element-2043">1.5x (Holiday)</option>
                </select>}
            </div>
          </div>

          <div className="overflow-x-auto" data-id="element-2044">
            <table className="w-full text-left border-collapse" data-id="element-2045">
              <thead className="bg-surface-warm border-b border-border" data-id="element-2046">
                <tr data-id="element-2047">
                  <th className="px-6 py-4 text-sm font-semibold text-text-secondary" data-id="element-2048">
                    Media Type
                  </th>
                  <th className="px-6 py-4 text-sm font-semibold text-text-secondary" data-id="element-2049">
                    Fee per Store per Week
                  </th>
                  {seasonalMultiplier !== 1.0 && <th className="px-6 py-4 text-sm font-semibold text-text-secondary" data-id="element-2050">
                      Adjusted Fee
                    </th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-border" data-id="element-2051">
                {mediaTypes.map(mediaType => {
                const basePrice = prices[mediaType];
                const finalPrice = basePrice * seasonalMultiplier;
                return <tr key={mediaType} className="hover:bg-surface/30" data-id="element-2052">
                      <td className="px-6 py-5 font-medium text-text-primary" data-id="element-2053">
                        {mediaType}
                      </td>
                      <td className="px-6 py-5" data-id="element-2054">
                        {readOnly ? <div className="text-sm font-medium text-text-primary" data-id="element-2055">
                            {formatCurrency(basePrice)}
                          </div> : <div className="relative max-w-[200px]" data-id="element-2056">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" data-id="element-2057">
                              R
                            </span>
                            <input type="text" value={basePrice.toLocaleString('en-ZA')} onChange={e => handlePriceChange(mediaType, e.target.value)} className="w-full pl-8 pr-3 py-2 border border-border rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary" data-id="element-2058" />
                          </div>}
                      </td>
                      {seasonalMultiplier !== 1.0 && <td className="px-6 py-5" data-id="element-2059">
                          <div className="text-sm font-medium text-text-primary" data-id="element-2060">
                            {formatCurrency(finalPrice)}
                          </div>
                        </td>}
                    </tr>;
              })}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>;
}